import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, FormGroup, Label, Row } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import Select from "react-select";
import html2canvas from "html2canvas";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import axios from "axios";

const BudgetReports = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = authUser?.token;
  const [regionData, setRegionData] = useState();
  const [regionNames, setRegionNames] = useState([]);
  const [regionBudget, setRegionBudget] = useState([]);
  const [utilizedBudget, setUtilizedBudget] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prevState) => !prevState);

  useEffect(() => {
    fetchRegions();
  }, []);

  useEffect(() => {
    if (regionData && regionOptions.length > 0) {
      handleRegionChange(regionOptions);
    }
  }, [regionData, regionOptions]);

  const fetchRegions = async () => {
    try {
      const regionResponse = await axios.get(`${process.env.REACT_APP_API_BASEURL}/region`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = regionResponse.data;
      setRegionData(data);
      setRegionNames(data.map((region) => region.regionName));
      setRegionBudget(data.map((region) => region.regionBudget));
      setUtilizedBudget(data.map((region) => region.utilizedBudget));

      const formattedRegions = regionResponse?.data.map((region) => ({
        value: region._id,
        label: region.regionName,
      }));
      setRegionOptions(formattedRegions);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRegionChange = (selectedOptions) => {
    if (!regionData) return;

    const selectedRegionIds = selectedOptions.map((option) => option.value);
    setSelectedRegions(selectedRegionIds);

    const filteredRegionData = regionData.filter((region) =>
      selectedRegionIds.includes(region._id)
    );

    const pieChartSeries = filteredRegionData.map((region) => {
      const allocatedBudget = region.regionBudget;
      const utilizedBudget = region.utilizedBudget;
      const isOverspend = utilizedBudget < 0;

      const pieSeries = [allocatedBudget, Math.abs(utilizedBudget)];
      const pieLabels = [
        `Allocated Budget`,
        isOverspend ? `Overspend` : `Utilized Budget`,
      ];

      return {
        regionName: region.regionName,
        series: pieSeries,
        options: {
          chart: {
            width: 380,
            type: "pie",
          },
          labels: pieLabels,
          colors: isOverspend ? ["#4e4e4e", "#FF0000"] : ["#4e4e4e", "#e42626"],
          responsive: [
            {
              breakpoint: 480,
              options: {
                chart: {
                  width: 200,
                },
                legend: {
                  position: "bottom",
                },
              },
            },
          ],
        },
      };
    });

    setPieData(pieChartSeries);
  };

  const handleExport = () => {
    const exportDiv = document.querySelector(".export");
    html2canvas(exportDiv, {
      useCORS: true,
      logging: true,
    }).then((canvas) => {
      const link = document.createElement("a");
      link.download = "budget_report.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
    });
  };

  const handleExportCSV = () => {
    const headers = "Region,Allocated Budget,Utilized Budget";

    const csvData = pieData.map((pieChart) => {
      const utilizedBudget = pieChart.series[1];
      const isOverspend = pieChart.options.labels.includes("Overspend");

      return {
        Region: pieChart.regionName,
        AllocatedBudget: pieChart.series[0],
        UtilizedBudget: isOverspend ? `-${utilizedBudget}` : utilizedBudget,
      };
    });

    const csvString = [
      headers,
      ...csvData.map((row) => Object.values(row).join(",")),
    ].join("\n");

    const csvBlob = new Blob([csvString], { type: "text/csv" });
    const csvUrl = URL.createObjectURL(csvBlob);

    const link = document.createElement("a");
    link.download = "budget_report.csv";
    link.href = csvUrl;
    link.click();
  };

  return (
    <React.Fragment>
      <Row>
        <Col xl={12}>
          <Card>
            <CardBody>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                  <h5 className="card-title">Budget Report</h5>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "10px",
                  }}
                >
                  <FormGroup>
                    <Select
                      showSearch
                      name="region"
                      options={regionOptions}
                      isMulti
                      className="basic-multi-select"
                      classNamePrefix="select"
                      onChange={(selectedOptions) =>
                        handleRegionChange(selectedOptions)
                      }
                      value={selectedRegions.map((regionId) => ({
                        value: regionId,
                        label: regionData.find(
                          (region) => region._id === regionId
                        ).regionName,
                      }))}
                      styles={{
                        menu: (provided) => ({
                          ...provided,
                          maxHeight: "100px",
                          overflowY: "auto",
                        }),
                        menuList: (provided) => ({
                          ...provided,
                          maxHeight: "100px",
                          overflowY: "auto",
                        }),
                      }}
                    />
                  </FormGroup>
                  <Dropdown isOpen={dropdownOpen} toggle={toggle}>
                    <DropdownToggle
                      style={{
                        backgroundColor: "#e42626",
                        minWidth: "110px",
                        borderRadius: "8px",
                        padding: "8px",
                        border: "none",
                        color: "white",
                        height: "35px",
                      }}
                    >
                      Export data
                    </DropdownToggle>
                    <DropdownMenu>
                      <DropdownItem>
                        <button
                          type="button"
                          style={{
                            backgroundColor: "transparent",
                            width: "max-content",
                            border: "none",
                          }}
                          onClick={handleExport}
                        >
                          Export PNG
                        </button>
                      </DropdownItem>
                      <DropdownItem>
                        {" "}
                        <button
                          type="button"
                          style={{
                            backgroundColor: "transparent",
                            width: "max-content",
                            border: "none",
                          }}
                          onClick={handleExportCSV}
                        >
                          Export CSV
                        </button>
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </div>
              <div
                style={{
                  borderRadius: "8px",
                  borderColor: "#f1f1f1",
                  borderWidth: "1px",
                  borderStyle: "solid",
                  display: "flex",
                  flexDirection: "row",
                  overflow: "hidden",
                  flexWrap: "wrap",
                }}
                className="export"
              >
                {pieData.map((pieChart, index) => (
                  <div
                    key={index}
                    style={{
                      margin: "10px",
                      width: "45%",
                    }}
                  >
                    <ReactApexChart
                      options={pieChart.options}
                      series={pieChart.series}
                      type="pie"
                    // width={380}
                    />
                    <div
                      style={{
                        marginTop: "10px",
                        textAlign: "center",
                      }}
                    >
                      <strong>{pieChart.regionName}</strong>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default BudgetReports;
